import { FC, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { useSidebar } from '~/layouts/private/providers/sidebar';

import { useAuth } from '~/common/providers/auth';
import { useToast } from '~/common/providers/toast';
import { ISidebarItem } from '~/common/interfaces/ISidebarItem';
import { UserSubscriptionTypes } from '~/common/enums/UserSubscriptionTypes';
import { GenericMenuItem } from '~/common/components/GenericMenuItem';
import { Modal } from '~/common/components/Modal';
import { Button } from '~/common/components/Button';
import { Typography } from '~/common/components/Typography';
import { useModal } from '~/common/hooks/use-modal';
import {
  IWeb3ConsentTerm,
  getWeb3ConsentStatus,
} from '~/common/services/controllers/web3-consent';

import { Web3ConsentTermModal } from './Web3ConsentTermModal';

interface ISidebarNavProps {
  nav: Omit<ISidebarItem, 'children'>;
  index: number;
}
export const SidebarNav: FC<ISidebarNavProps> = ({ nav, index }) => {
  const {
    user: { subscription } = {},
    canUseWallet,
    isWalletBetaTester,
    hasWeb3Consent,
    setHasWeb3Consent,
  } = useAuth();
  const { toggleSatoshiAiDrawer, collapsed } = useSidebar();
  const { showToast } = useToast();
  const router = useRouter();
  const { isModalOpen, handleOpenModal, handleCloseModal } = useModal();
  const [consentTerm, setConsentTerm] = useState<IWeb3ConsentTerm | null>(
    null
  );
  const [consentModalOpen, setConsentModalOpen] = useState<boolean>(false);

  const selected = useMemo(() => {
    if (!router || !router.asPath) {
      return false;
    }

    return router.asPath.replace('/', '') === nav.key;
  }, [router, nav]);

  const handleWalletItemClick = async () => {
    if (!canUseWallet) {
      handleOpenModal();
      return;
    }

    if (hasWeb3Consent) {
      window.location.href = nav.externalHref;
      return;
    }

    try {
      const status = await getWeb3ConsentStatus();

      if (status.accepted) {
        setHasWeb3Consent(true);
        window.location.href = nav.externalHref;
        return;
      }

      setConsentTerm(status.term);
      setConsentModalOpen(true);
    } catch {
      showToast({
        type: 'error',
        position: 'top-right',
        title: 'Erro ao carregar',
        description: 'Não foi possível carregar o termo de consentimento.',
      });
    }
  };

  return (
    <>
      <GenericMenuItem
        id={nav.key}
        compact={collapsed}
        icon={nav.icon}
        customIcon={nav.customIcon}
        title={nav.label}
        index={index}
        key={String(index)}
        selected={selected}
        {...(nav.key === 'satoshi-ai'
          ? {
              onClick: toggleSatoshiAiDrawer,
            }
          : nav.externalHref &&
            (subscription === UserSubscriptionTypes.admin || isWalletBetaTester)
          ? {
              onClick: handleWalletItemClick,
            }
          : {
              href: `/${nav.key}`,
            })}
        {...(nav.isNew && {
          badge: {
            variant: 'light',
            color: 'green',
            text: 'NEW',
          },
        })}
      />
      {nav.externalHref && (
        <Modal
          id={`wallet-restriction-${nav.key}`}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          width="480px"
          height="auto"
          title="Acesso Restrito"
          closeOnOutsideClick
          footer={
            <Button
              width="100%"
              text="Entendi"
              justifyContent="center"
              onClick={handleCloseModal}
            />
          }
        >
          <div style={{ padding: '16px 0' }}>
            <Typography.Label size="medium" color="neutral.300">
              O acesso às funcionalidades de Carteira, Caixinha e Trocar é
              exclusivo para assinantes do plano Platinum com no mínimo 7 dias de
              assinatura ativa.
            </Typography.Label>
          </div>
        </Modal>
      )}
      {nav.externalHref && (
        <Web3ConsentTermModal
          isOpen={consentModalOpen}
          term={consentTerm}
          onClose={() => setConsentModalOpen(false)}
          onConsented={() => {
            setHasWeb3Consent(true);
            setConsentModalOpen(false);
            window.location.href = nav.externalHref;
          }}
        />
      )}
    </>
  );
};
